import { cn, focusRing } from "@simpro/ui";
import { Link } from "react-router";
import { ArticleCard, type ArticleSummary } from "@/features/articles";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";

export interface ArticlesTeaserProps {
  heading: string | null;
  text: string | null;
  articles: readonly ArticleSummary[];
}

/**
 * "From the chair" on home (design note salon-home-landing.md 9.2; TDD-SA FR-SA-192): the three newest
 * articles as cards, with the salon's heading and line, and View all. Nothing until one is published.
 * The cards rise in turn as they come into view (design note salon-booking-premium-redesign.md
 * section 5.3 item 11).
 */
export function ArticlesTeaser({ heading, text, articles }: ArticlesTeaserProps) {
  if (articles.length === 0) return null;
  return (
    <section aria-labelledby="articles-heading" className={cn(page, "flex flex-col gap-10 lg:gap-14")}>
      <SectionHeading
        id="articles-heading"
        eyebrow="Journal"
        title={heading ?? "From the chair: notes & tips"}
        lead={text}
        action={
          <Link to="/articles" viewTransition className={cn(pill.base, pill.md, pill.outline, focusRing)}>
            View all{" "}<span className="sr-only">articles</span>
          </Link>
        }
      />
      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {articles.map((article, index) => (
          <Reveal as="li" key={article.id} index={index}>
            <ArticleCard article={article} />
          </Reveal>
        ))}
      </ul>
    </section>
  );
}
